import type { components } from '@/lib/api-types-expenses-reports';

import type { components as Wave2 } from '@/lib/api-types-expenses-wave2';
import type { components as Parity } from '@/lib/api-types-expenses-parity';

type GeneratedReport = components['schemas']['Report'];

/** Wave 2 R-a: the purpose, the note type and the trip a report and a list item carry. */
export type NoteType = Wave2['schemas']['NoteType'];
export type TripScope = Wave2['schemas']['TripScope'];
export type PurposeChoice = Wave2['schemas']['PurposeChoice'];
export type ReportPurposeFields = Wave2['schemas']['ReportWave2Fields'];
export type ReportInputWave2 = Wave2['schemas']['ReportInputWave2'];

/** Reserved PDF blockers on reports and list items; currently always empty. */
export type PdfBlockers = Wave2['schemas']['PdfBlockers'];
/**
 * R-d: the PDF fields wave 2 adds to the report and to each list item. Optional here, as release 2's
 * fields are: a page reads them with `=== true` and `?? []`, so a view without them hides the PDF.
 */
type ReportPdfFields = Wave2['schemas']['ReportWave2'];
type SummaryPdfFields = Wave2['schemas']['ReportSummaryWave2'];

/** A report's expense row: the contract leaves every field optional; the server always sends these. */
export type ReportExpenseRow = {
  id: string;
  date: string;
  supplierName?: string | null;
  category: string;
  description?: string | null;
  /** A trip's «from → to» and km, when the view sends them (release 3). */
  kind?: 'standard' | 'mileage';
  route?: string | null;
  km?: string | null;
  total: string;
  status: string;
  documentStatus: string;
  documentMissing: boolean;
  reimbursable: boolean;
  rejected: boolean;
  /** The approver's decision on this expense, with the note they wrote (B2). */
  rejection?: ExpenseDecision;
  reimbursableOverride?: ExpenseDecision;
  can?: { reject?: boolean; makeReimbursable?: boolean };
  /** #915 C4: present for Gestione and the note's approvers only; null is «Nessuna». */
  internalClass?: Parity['schemas']['InternalClass'];
};
export type ExpenseDecision = { by?: string; at?: string; note?: string | null } | null;
/** #915 C1 and C3: the note's projects with %, its client and how many files of its own it holds. */
export type ReportParity = Parity['schemas']['ReportParityFields'];
export type NoteProject = ReportParity['projects'][number];
export type NoteClient = NonNullable<ReportParity['client']>;

export type Report = Omit<GeneratedReport, 'expenses' | 'can'> &
  ReportPurposeFields &
  Partial<Pick<ReportParity, 'projects' | 'client' | 'documentCount'>> & {
    expenses?: ReportExpenseRow[];
    /** `recordPayment` is in the API's view (B3) but not yet in the generated schema. */
    can?: NonNullable<GeneratedReport['can']> &
      Partial<ReportPdfFields['can']> & { recordPayment?: boolean };
    /** Why the PDF cannot be made right now, while `can.downloadPdf` says the caller may have it. */
    pdfBlockers?: ReportPdfFields['pdfBlockers'];
  };
export type ReportSummary = components['schemas']['ReportSummary'] &
  ReportPurposeFields & {
    can?: Partial<SummaryPdfFields['can']>;
    pdfBlockers?: SummaryPdfFields['pdfBlockers'];
  };
export type ReportPage = components['schemas']['ReportPage'];
export type ReportStatus = components['schemas']['ReportStatus'];
export type SubmitBlocker = components['schemas']['SubmitBlocker'];
export type Person = components['schemas']['Person'];

/** Every status the list's «Stato» filter offers, in the order of the board (spec §1.1). */
export const REPORT_STATUSES: readonly ReportStatus[] = [
  'draft',
  'submitted',
  'more_info_requested',
  'accepted',
  'refused',
  'partially_reimbursed',
  'reimbursed',
  'repayment_requested',
  'repaid',
];

/** A report the owner still edits: the page offers its actions only in these (spec §1.3). */
export function isOpen(status: ReportStatus): boolean {
  return status === 'draft' || status === 'more_info_requested';
}

/**
 * The list the board draws: drafts first for whoever may edit them; on a phone «Da rivedere»
 * comes next (spec §1.2). The sort is stable, so the server's newest-month order holds inside
 * each group.
 */
export function sortForOwner(
  items: readonly ReportSummary[],
  options: { readonly reviewFirst: boolean },
): ReportSummary[] {
  const rank = (report: ReportSummary): number => {
    if (report.can?.edit !== true) return 2;
    if (report.status === 'draft') return 0;
    if (options.reviewFirst && report.status === 'more_info_requested') return 1;
    return 2;
  };
  return [...items].sort((a, b) => rank(a) - rank(b));
}
